import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import ProcedurePlaceholder from "@/components/ProcedurePlaceholder";
import { HERO } from "@/data/procedureHeroImages";
import PatientTestimonials from "../../components/PatientTestimonials";
import SEO from "@/components/SEO";
import { WHATSAPP_URL, TEL_URL, PHONE_DISPLAY } from "@/constants/contact";
import {
  ArrowRight,
  Award,
  BedDouble,
  ChevronRight,
  CircleDot,
  Clock,
  GraduationCap,
  Layers,
  MessageCircle,
  Phone,
  Minus,
  Monitor,
  PersonStanding,
  Plus,
  Sparkles,
  User,
  Zap,
} from "lucide-react";

const PAGE_URL = "https://drvickyjain.com/hair-transplant-mumbai";
const PRP_PATH = "/prp-therapy-hair-loss-mumbai";

const HAIR_TRANSPLANT_FAQS = [
  {
    question: "Is hair transplant permanent?",
    answer:
      "Yes. The transplanted follicles are taken from the donor area at the back and sides of the scalp where hair is genetically resistant to the DHT hormone that causes pattern baldness. These follicles retain their genetic resistance in their new location and do not fall out. However, native hair in the recipient area that has not been transplanted may continue to thin over time with natural hair loss progression.",
  },
  {
    question: "How many grafts do I need?",
    answer:
      "This depends on the extent of hair loss, the density of the donor area, and the desired coverage. Frontal hairline restoration typically requires 1,500 to 2,500 grafts. Crown coverage requires 1,500 to 3,000 grafts. Combined frontal and crown work in a single session may require 3,000 to 4,500 grafts. Dr. Jain calculates the graft requirement during consultation based on measurement of the recipient area and assessment of donor density.",
  },
  {
    question: "When will I see results after hair transplant?",
    answer:
      "Visible growth begins at 3 to 4 months. Significant improvement is apparent at 6 months. Full density and the final natural result are visible at 12 months. Patients who assess results before 12 months are evaluating an incomplete outcome.",
  },
  {
    question: "Is there a scar after FUE hair transplant?",
    answer:
      "FUE leaves tiny circular marks at each extraction site in the donor area. These are 0.7 to 1.0 millimeters and are not visible when hair is at normal length. They are visible if the head is shaved completely. FUT leaves a linear scar at the donor site that is concealed by surrounding hair at lengths above approximately 1.5 centimeters.",
  },
  {
    question: "Can transplanted hair fall out again?",
    answer:
      "No. Transplanted follicles from the genetically resistant donor zone do not fall out. They grow permanently in their new location. However, native non-transplanted hair in the surrounding area may continue to thin with ongoing natural hair loss. This is why planning accounts for expected future loss so the transplanted design remains appropriate as native hair changes over time.",
  },
  {
    question: "Is hair transplant painful?",
    answer:
      "The procedure is performed under local anesthesia. The anesthesia injections to the scalp produce brief discomfort. The extraction and implantation phases are painless. Post-procedure soreness and mild swelling are managed with standard medication for the first 2 to 3 days.",
  },
  {
    question: "Can hair transplant be combined with PRP therapy?",
    answer:
      "Yes, and this is a common approach. PRP therapy applied around the time of transplant may improve graft survival by increasing growth factor concentration in the recipient tissue. Post-transplant PRP sessions help maintain native hair density in surrounding areas where loss is progressing. The combination addresses both the restored area through transplant and the at-risk native hair through medical management.",
  },
  {
    question: "How do I know if I have enough donor hair for a transplant?",
    answer:
      "Donor assessment is performed during consultation. Dr. Jain examines the density, caliber, and total available area of donor hair at the back and sides of the scalp. These measurements determine the maximum number of grafts safely extractable from the donor zone. Patients with very low donor density relative to the extent of their hair loss may not have sufficient donor supply for full coverage, and this is communicated directly during consultation with realistic alternative options discussed.",
  },
];

const structuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "MedicalProcedure",
      name: "Hair transplant",
      description:
        "Surgical relocation of hair follicles from the donor scalp to thinning or bald areas using FUE, FUT, or DHI; customized per patient for natural hairline and density.",
      procedureType: "SurgicalProcedure",
      bodyLocation: { "@type": "AnatomicalStructure", name: "Scalp" },
      url: PAGE_URL,
      medicalSpecialty: "https://schema.org/PlasticSurgery",
    },
    {
      "@type": "MedicalProcedure",
      name: "Beard transplant",
      description:
        "Hair transplant of scalp follicles to the beard area to improve density, define the beard line, or fill patches; permanent result requiring routine grooming.",
      procedureType: "SurgicalProcedure",
      bodyLocation: { "@type": "AnatomicalStructure", name: "Beard" },
      url: PAGE_URL,
      medicalSpecialty: "https://schema.org/PlasticSurgery",
    },
    {
      "@type": "MedicalProcedure",
      name: "Eyebrow transplant",
      description:
        "Precision hair transplant to the eyebrow region with controlled angle and direction for thinning or absent eyebrows.",
      procedureType: "SurgicalProcedure",
      bodyLocation: { "@type": "AnatomicalStructure", name: "Eyebrow" },
      url: PAGE_URL,
      medicalSpecialty: "https://schema.org/PlasticSurgery",
    },
    {
      "@type": "FAQPage",
      mainEntity: HAIR_TRANSPLANT_FAQS.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: { "@type": "Answer", text: faq.answer },
      })),
    },
  ],
};

function HairTransplantJsonLd() {
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />;
}

function Breadcrumb() {
  const items = [
    { name: "Home", href: "/" },
    { name: "Non-Surgical", href: "/non-surgical-treatments-in-mumbai" },
    { name: "Hair Transplant", href: "/hair-transplant-mumbai" },
  ];
  return (
    <nav className="bg-warm-white pt-24 pb-4" data-testid="breadcrumb">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <ol className="flex flex-wrap items-center gap-2 text-sm text-deep-navy/60">
          {items.map((item, index) => (
            <li key={item.name} className="flex items-center gap-2">
              {index > 0 && <ChevronRight className="w-3 h-3 text-gold" />}
              {index === items.length - 1 ? (
                <span className="text-deep-navy/70">{item.name}</span>
              ) : (
                <Link to={item.href} className="hover:text-gold transition-colors">
                  {item.name}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
}

function HeroSection() {
  return (
    <section className="bg-warm-white pt-8 md:pt-10 pb-12 md:pb-20" data-testid="procedure-hero">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Non-Surgical · Hair</p>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-premium-purple leading-[1.08] mb-4">Hair Transplant</h1>
            <div className="w-12 h-[2px] bg-gold mb-5" />
            <p className="text-lg sm:text-xl text-deep-navy/65 leading-relaxed mb-7 max-w-xl font-light">
              FUE, FUT &amp; DHI Hair Restoration by Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon
            </p>
            <div className="flex flex-wrap gap-2">
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">Board Certified</span>
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">17+ Years Experience</span>
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">International Training</span>
            </div>
          </div>
          <div className="flex justify-center lg:justify-end">
            <ProcedurePlaceholder procedureName="Hair Transplant" icon="💇" size="hero"  imageSrc={HERO.hairFallHairLoss} imageAlt="Hair transplant" />
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatIsSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="what-is-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Overview</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">What is a Hair Transplant?</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              A hair transplant is a minimally invasive surgical procedure that moves healthy hair follicles from a donor area, typically the back and sides of the scalp where hair is genetically resistant to loss, to areas of thinning or baldness. The transplanted follicles retain their genetic resistance to hair loss in their new location, which is why the results are permanent. The transplanted hair grows, can be cut and styled, and behaves identically to natural hair.
            </p>
            <p>
              Hair loss affects both men and women and its causes range from genetics to hormonal change, stress, medication, and scarring from previous procedures. The pattern and degree of loss determine how many grafts are needed, which technique is appropriate, and what result is achievable. Not every patient is a good candidate for transplant and not every area of hair loss can be fully restored. Dr. Jain assesses donor density, recipient area demand, and realistic outcome expectations at every consultation before a surgical plan is proposed.
            </p>
            <p>
              Dr. Vicky Jain performs hair transplant using FUE, FUT, and DHI techniques depending on the patient&apos;s graft requirements, donor area characteristics, and the degree of precision needed in the recipient area. Hairline design is planned individually based on facial structure, age, and ethnicity before any grafts are extracted.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function DrJainApproachSection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="dr-jain-approach-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Our Approach</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-8">Dr. Jain&apos;s Approach to Hair Transplant Surgery</h2>
            <div className="space-y-6 text-white/75 leading-relaxed">
              <p>
                The hairline is the most visible element of a hair transplant result. A hairline that is placed too low, too straight, or without the natural irregularity of organic hair growth looks operated on regardless of how technically well the procedure was executed. Dr. Jain designs the hairline with the patient before surgery, accounting for facial symmetry, the patient&apos;s age, the expected progression of native hair loss, and the ethnicity-specific patterns of natural hairline position. A hairline appropriate for a 30-year-old in a 60-year-old patient looks wrong. A hairline designed for the patient&apos;s current and future hair loss pattern looks right.
              </p>
              <p>
                Graft survival depends on the time between extraction and implantation, the handling technique during processing, and the depth and angle of the recipient site channels. Dr. Jain coordinates the extraction and implantation stages to minimize graft out-of-body time and uses precise channel creation to match the natural growth angle of the surrounding hair. The direction, angle, and density of implanted grafts determine whether the result looks natural or artificial at close range. These details are not visible in the before and after photos. They are the difference between a result that deceives and one that does not.
              </p>
            </div>
          </div>
          <div className="relative">
            <img src="/dr-jain.avif" alt="Dr. Vicky Jain" loading="lazy" decoding="async" className="w-full h-[420px] object-cover object-top" />
            <div
              className="absolute top-0 left-0 right-0 h-[420px] pointer-events-none hidden lg:block"
              style={{ background: "linear-gradient(to right, #3D2566 0%, rgba(61, 37, 102, 0) 15%)" }}
            />
            <div className="mt-4 text-center lg:text-left">
              <p className="font-serif text-lg text-white/80">Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function CandidateGridSection() {
  const cards = [
    {
      title: "Male Pattern Baldness",
      icon: User,
      copy: "The most common indication. Androgenetic alopecia in men produces a predictable pattern of frontal recession and crown thinning. The back and sides of the scalp retain genetically resistant follicles that serve as the donor supply. Norwood scale Grade 2 to 6 patients are typically good candidates depending on donor density.",
    },
    {
      title: "Female Hair Thinning",
      icon: Sparkles,
      copy: "Diffuse thinning, widening parting, and frontal hairline recession in women. Female pattern hair loss is more diffuse than male pattern baldness and donor assessment is more complex. Many women with stable, localized thinning and adequate donor density are good candidates.",
    },
    {
      title: "Beard Transplant",
      icon: Layers,
      copy: "Patchy beard due to genetics, scarring, or alopecia. Hair follicles from the scalp are transplanted to create a defined beard line, fill patches, or build a complete beard where none exists. The result is permanent beard hair that grows and requires shaving.",
    },
    {
      title: "Eyebrow Transplant",
      icon: CircleDot,
      copy: "Thinning or absent eyebrows from over-plucking, alopecia, thyroid conditions, or scarring. Scalp follicles are transplanted into the eyebrow area with precise angle and direction control. The transplanted hair grows and requires periodic trimming.",
    },
    {
      title: "Hair Loss from Scarring",
      icon: Zap,
      copy: "Scarring from burns, trauma, previous surgery, or radiation creates areas of permanent hair loss where follicles have been destroyed. Hair transplant into scarred areas is technically more demanding due to altered vascularity but is appropriate in selected cases.",
    },
    {
      title: "Hairline Lowering",
      icon: Monitor,
      copy: "Patients with a naturally high hairline who want to lower and reshape it without a surgical hairline lowering procedure. Transplanting grafts into the hairline zone gradually lowers and refines the hairline over one or two sessions.",
    },
  ];
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="candidate-grid-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Indications</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Who is a Good Candidate for Hair Transplant?</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {cards.map((card) => (
            <div key={card.title} className="bg-white p-8 border border-[#E8E2D9]">
              <div className="w-12 h-12 bg-gold/10 flex items-center justify-center mb-4" aria-hidden>
                <card.icon className="w-6 h-6 text-gold" />
              </div>
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{card.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed text-sm md:text-base">{card.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TechniquesSection() {
  const blocks = [
    {
      title: "FUE (Follicular Unit Extraction)",
      copy: "The most widely used modern technique. Individual follicular units are extracted one by one from the donor area using a micro-punch of 0.7 to 1.0 millimeters. No linear incision is made and no stitches are required. The donor area heals with tiny circular marks that are not visible once hair grows back to normal length. FUE is appropriate for most patients and allows targeted extraction from the densest donor areas. Best for patients requiring up to approximately 3,000 to 4,000 grafts per session.",
    },
    {
      title: "FUT (Follicular Unit Transplantation)",
      copy: "A strip of scalp is surgically removed from the donor area, closed with sutures, and the individual follicular units are dissected from the strip under microscopic magnification before implantation. Produces a linear scar at the donor site that is concealed by surrounding hair at normal lengths. Allows a higher number of grafts per session than FUE. Best for patients requiring large graft numbers in a single session who are comfortable keeping hair at a length that conceals the donor scar.",
    },
    {
      title: "DHI (Direct Hair Implantation)",
      copy: "A variation of FUE where extracted follicles are implanted directly using a specialized implanter pen without the need to pre-create recipient channels. Allows precise control of depth, angle, and direction during implantation. Particularly suited to hairline work, eyebrow transplantation, and areas where high precision and natural direction are critical. Can also be performed on existing hair without shaving the recipient area.",
    },
    {
      title: "Sapphire FUE",
      copy: "Uses sapphire-tipped blades rather than steel to create recipient channels. The sharper, smoother incision creates less tissue trauma, smaller channels, and potentially better graft survival compared to standard steel blade channel creation. The recipient site heals faster with less inflammation. Used as an upgrade within the FUE framework for patients who want optimal graft survival.",
    },
  ];
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="techniques-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Procedure Options</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Hair Transplant Techniques</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {blocks.map((block) => (
            <div key={block.title} className="bg-white p-8 border border-[#E8E2D9]">
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{block.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{block.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function DifferentAreasSection() {
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="different-areas-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Treatment Areas</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">Hair Transplant for Scalp, Beard, and Eyebrows</h2>
          </div>
          <div className="space-y-8 text-deep-navy/70 leading-relaxed">
            <div>
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">Scalp Hair Transplant</h3>
              <p>
                Addresses frontal hairline recession, crown thinning, and diffuse scalp thinning depending on the pattern of loss. The hairline zone requires the most precise graft placement for a natural result. Crown transplantation requires efficient coverage of a large area with appropriate graft density. The combination of both in a single session requires careful graft allocation planning to serve both zones adequately.
              </p>
            </div>
            <div>
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">Beard Transplant</h3>
              <p>
                Scalp follicles are transplanted to the beard area to create definition, fill patches, or establish a complete beard line. The grafts are placed at a shallow angle matching the natural beard growth direction. The transplanted hair grows as beard hair and requires regular maintenance including shaving. Results are visible from 4 to 6 months with full density at 12 months.
              </p>
            </div>
            <div>
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">Eyebrow Transplant</h3>
              <p>
                Requires the most precise angle and direction control of any transplant area. Eyebrow hair grows at an extremely shallow angle, almost parallel to the skin surface. Incorrect angle placement produces upward-growing grafts that look obviously transplanted. DHI technique is preferred for eyebrow work because the implanter pen allows the most precise angle control during implantation.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function HowPerformedSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="how-performed-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">The Procedure</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">How Hair Transplant Surgery is Performed</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              The procedure is performed under local anesthesia. Patients are awake throughout and experience no pain during the procedure. Mild discomfort during the initial anesthesia injections is the only painful element. The total procedure time ranges from 6 to 10 hours for a full session depending on graft count and technique.
            </p>
            <p>
              After anesthesia, the donor area is prepared and grafts are extracted according to the planned technique. FUE extraction is performed systematically across the donor zone to maintain uniform density rather than creating visible depleted patches. Extracted grafts are stored in a holding solution that maintains their viability during the processing period.
            </p>
            <p>
              Recipient channels are created at the planned density, angle, and direction within the hairline design marked before surgery. Grafts are then implanted one by one into these channels. Single-hair follicles are placed at the very front of the hairline for a natural, soft leading edge. Multi-hair follicles are placed behind the front row to build density. Patients go home the same day with written post-operative care instructions.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function ResultsTimelineSection() {
  const milestones = [
    {
      period: "Weeks 1 to 2",
      description:
        "Mild redness and scabbing at the recipient site. Normal healing. Grafts are anchored during this period. Gentle washing begins from day 3 following the protocol provided.",
      icon: BedDouble,
    },
    {
      period: "Weeks 2 to 6",
      description:
        "Shock loss phase. The transplanted hairs shed while the follicles remain anchored in the scalp. This is expected and does not indicate graft failure. The follicles are alive beneath the surface.",
      icon: PersonStanding,
    },
    {
      period: "Months 3 to 4",
      description:
        "New hair growth begins. Fine, thin hairs emerge from the transplanted follicles. Growth is uneven at this stage.",
      icon: Monitor,
    },
    {
      period: "Months 6 to 12",
      description:
        "Hair thickens and grows at its natural rate. The result becomes increasingly visible. Final density and natural appearance are assessed at 12 months when all transplanted follicles have completed their growth cycle.",
      icon: Sparkles,
    },
  ];
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="results-timeline-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">What to Expect</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-12 text-center">Hair Transplant Results Timeline</h2>
        <div className="hidden md:block relative">
          <div className="absolute top-16 left-0 right-0 h-0.5 bg-deep-navy/10" />
          <div className="grid grid-cols-4 gap-4">
            {milestones.map((milestone, index) => (
              <div key={index} className="relative">
                <div className="flex justify-center mb-3">
                  <milestone.icon className="w-6 h-6 text-deep-navy/40" />
                </div>
                <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center mx-auto mb-4 relative z-10">
                  <div className="w-3 h-3 rounded-full bg-warm-white" />
                </div>
                <div className="text-center">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="md:hidden relative pl-12">
          <div className="absolute top-0 bottom-0 left-4 w-0.5 bg-deep-navy/10" />
          <div className="space-y-8">
            {milestones.map((milestone, index) => (
              <div key={index} className="relative">
                <div className="absolute -left-12 top-0 flex flex-col items-center">
                  <milestone.icon className="w-5 h-5 text-deep-navy/40 mb-1" />
                  <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center">
                    <div className="w-3 h-3 rounded-full bg-warm-white" />
                  </div>
                </div>
                <div className="pl-4">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function ComparisonSection() {
  const rows = [
    {
      label: "Result Type",
      transplant: "Permanent. Transplanted follicles do not fall out.",
      meds: "Temporary maintenance. Hair loss resumes if medication stops.",
      prp: "Mild improvement in existing hair density. Does not replace lost follicles.",
    },
    {
      label: "Duration",
      transplant: "One-time procedure with permanent result.",
      meds: "Continuous daily use indefinitely.",
      prp: "Multiple sessions, typically 3 to 6 initially then maintenance.",
    },
    {
      label: "New Hair Growth",
      transplant: "Yes. New permanent hair in previously bald areas.",
      meds: "Slows loss and may thicken existing hair. Does not restore follicles in bald areas.",
      prp: "Improves thickness of existing hair. Does not restore follicles.",
    },
    {
      label: "Best Used For",
      transplant: "Established bald areas and receded hairlines where follicles are no longer present.",
      meds: "Early hair loss prevention and slowing progression.",
      prp: "Maintaining existing hair and improving thickness alongside transplant.",
    },
  ];
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="comparison-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Understanding Your Options</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Hair Transplant vs. Other Hair Loss Treatments</h2>
        <div className="max-w-6xl mx-auto border border-[#E8E2D9] bg-white overflow-hidden overflow-x-auto">
          <div className="hidden lg:grid lg:grid-cols-4 gap-0 border-b border-[#E8E2D9] bg-premium-purple text-warm-white min-w-[720px]">
            <div className="p-4 font-serif text-sm" />
            <div className="p-4 font-serif text-sm border-l border-warm-white/20">Hair Transplant</div>
            <div className="p-4 font-serif text-sm border-l border-warm-white/20">Medications (Minoxidil/Finasteride)</div>
            <div className="p-4 font-serif text-sm border-l border-warm-white/20">
              PRP Therapy
            </div>
          </div>
          {rows.map((row) => (
            <div key={row.label} className="grid grid-cols-1 lg:grid-cols-4 gap-0 border-b border-[#E8E2D9] last:border-b-0 min-w-0">
              <div className="p-4 lg:p-5 bg-warm-white/80 font-medium text-deep-navy text-sm lg:text-base">{row.label}</div>
              <div className="p-4 lg:p-5 border-t lg:border-t-0 lg:border-l border-[#E8E2D9] text-deep-navy/70 text-sm leading-relaxed">
                <span className="lg:hidden font-serif text-premium-purple block mb-2">Hair Transplant</span>
                {row.transplant}
              </div>
              <div className="p-4 lg:p-5 border-t lg:border-t-0 lg:border-l border-[#E8E2D9] text-deep-navy/70 text-sm leading-relaxed">
                <span className="lg:hidden font-serif text-premium-purple block mb-2">Medications (Minoxidil/Finasteride)</span>
                {row.meds}
              </div>
              <div className="p-4 lg:p-5 border-t lg:border-t-0 lg:border-l border-[#E8E2D9] text-deep-navy/70 text-sm leading-relaxed">
                <span className="lg:hidden font-serif text-premium-purple block mb-2">PRP Therapy</span>
                {row.prp}
              </div>
            </div>
          ))}
        </div>
        <p className="max-w-6xl mx-auto mt-8 text-deep-navy/70 text-sm md:text-base leading-relaxed text-center lg:text-left">
          Medications and{" "}
          <Link to={PRP_PATH} className="text-gold font-medium hover:text-deep-navy underline underline-offset-2 transition-colors">
            PRP therapy
          </Link>{" "}
          are not alternatives to hair transplant for established baldness. They are appropriate for early hair loss prevention and as adjuncts to transplant to maintain native hair and improve graft survival. Patients with significant bald areas are not served by non-surgical treatments alone.
        </p>
      </div>
    </section>
  );
}

function PostProcedureCareSection() {
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="post-procedure-care-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Post-Op Care</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">Hair Transplant Aftercare</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              The first 14 days after surgery are the most critical for graft survival. The transplanted follicles are anchored but vulnerable to physical displacement during this period. Dr. Jain provides a written aftercare protocol that specifies washing technique, sleeping position, activity restrictions, and what to expect at each stage of healing.
            </p>
            <p>
              Key requirements include sleeping with the head elevated for the first week to reduce scalp swelling, avoiding direct water pressure on the recipient area for the first 3 days, washing with a gentle patting technique from day 3 onward, avoiding gym, swimming, and strenuous activity for 2 weeks, and protecting the scalp from direct sun exposure for at least 4 weeks. Medications prescribed at discharge include anti-inflammatory medication, antibiotics, and pain relief for the first few days.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function CandidateProseSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="candidate-prose-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Am I a Candidate?</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">Who Is a Good Candidate for Hair Transplant Surgery?</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Suitability is determined by donor supply, the pattern and stability of hair loss, general health, and whether your goals match what surgery can achieve. During consultation, Dr. Jain measures donor density and caliber, maps the recipient area, and discusses how future native hair loss may affect the long-term appearance of the result. Patients with active, unstable inflammatory scalp disease, unrealistic density expectations, or insufficient donor hair relative to the area to cover may be advised to defer surgery, stage treatment, or combine transplant with medical therapy such as{" "}
              <Link to={PRP_PATH} className="text-gold font-medium hover:text-deep-navy underline underline-offset-2 transition-colors">
                PRP
              </Link>{" "}
              or medications to protect remaining hair.
            </p>
            <p>
              Non-smokers and those who can follow aftercare closely—including limited exercise and sun exposure in the early weeks—heal more predictably. Beard and eyebrow transplant candidates need healthy donor scalp hair and clear goals for shape and density. If you are considering hair restoration, a structured assessment ensures you understand graft counts, technique choice (FUE, FUT, or DHI), timeline to full results at 12 months, and how adjunct treatments support native hair alongside transplanted follicles.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

const PatientResultsSection = () => <PatientTestimonials procedureName="Hair" />;

function FAQSection() {
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="faq-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Common Questions</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Frequently Asked Questions</h2>
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full">
            {HAIR_TRANSPLANT_FAQS.map((faq, index) => (
              <AccordionItem key={faq.question} value={`ht-${index}`} className="border-b border-deep-navy/10">
                <AccordionTrigger className="text-left font-serif text-lg text-premium-purple hover:no-underline hover:text-gold py-6 [&[data-state=open]>div>svg:first-child]:hidden [&[data-state=open]>div>svg:last-child]:block [&>svg]:hidden">
                  <span className="pr-4">{faq.question}</span>
                  <div className="flex-shrink-0">
                    <Plus className="w-5 h-5 text-gold" />
                    <Minus className="w-5 h-5 text-gold hidden" />
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-deep-navy/70 leading-relaxed pb-6">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

function ConsultationCTASection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="consultation-cta-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
        <h2 className="font-serif text-3xl md:text-4xl text-warm-white mb-4">Book Your Hair Transplant Consultation</h2>
        <p className="text-warm-white/70 mb-8 max-w-2xl mx-auto leading-relaxed">
          During your consultation, Dr. Jain assesses your hair loss pattern, donor density, scalp condition, and realistic graft requirements to determine whether transplant is appropriate and what result is achievable for your specific anatomy. A hairline design is discussed before any surgical plan is proposed. You leave with a clear picture of what the procedure involves, what the result looks like, and what timeline to expect.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
          <Button asChild className="bg-gold text-white hover:bg-gold-hover border-none rounded-none px-8 py-6 text-base">
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              Book Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </Button>
          <Button
            asChild
            variant="outline"
            className="border-warm-white text-warm-white hover:bg-warm-white hover:text-deep-navy rounded-none px-8 py-6 text-base"
          >
            <a href={TEL_URL}>
              <Phone className="w-5 h-5 mr-2" />
              Call {PHONE_DISPLAY}
            </a>
          </Button>
        </div>
      </div>
    </section>
  );
}

function RelatedProceduresSection() {
  const procedures = [
    {
      name: "Rhinoplasty",
      description: "Facial balance and proportion alongside hair restoration for complete facial aesthetic improvement.",
      href: "/rhinoplasty-in-mumbai",
    },
    {
      name: "Facelift",
      description: "Complete facial rejuvenation for patients addressing hair restoration alongside facial aging.",
      href: "/facelift-surgery-mumbai",
    },
    {
      name: "Gynecomastia Surgery",
      description: "For male patients combining chest correction with hair restoration in their aesthetic plan.",
      href: "/gynecomastia-surgery-mumbai",
    },
  ];
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="related-procedures-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Related Procedures</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Related Procedures</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {procedures.map((procedure) => (
            <div
              key={procedure.name}
              className="bg-white p-8 border border-[#E8E2D9] hover:shadow-lg transition-shadow duration-300 group"
            >
              <h3 className="font-serif text-xl text-premium-purple mb-3 font-semibold tracking-tight">{procedure.name}</h3>
              <p className="text-deep-navy/60 mb-6 leading-relaxed">{procedure.description}</p>
              <Link
                to={procedure.href}
                className="inline-flex items-center text-deep-navy font-medium hover:text-gold transition-colors"
              >
                Learn More →
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function HairTransplantPage() {
  return (
    <>
      <SEO
        title="Hair Transplant | FUE & DHI by Dr. Vicky Jain"
        description="Permanent hair restoration for male pattern baldness, female hair thinning, beard, and eyebrow loss. Dr. Vicky Jain performs hair transplant. Book today."
        canonical="/hair-transplant-mumbai"
      />
      <HairTransplantJsonLd />
      <Breadcrumb />
      <HeroSection />
      <WhatIsSection />
      <DrJainApproachSection />
      <CandidateGridSection />
      <TechniquesSection />
      <DifferentAreasSection />
      <HowPerformedSection />
      <ResultsTimelineSection />
      <ComparisonSection />
      <PostProcedureCareSection />
      <CandidateProseSection />
      <PatientResultsSection />
      <FAQSection />
      <ConsultationCTASection />
      <RelatedProceduresSection />
    </>
  );
}
